import { useCallback } from 'react';

import { useTrackUploadAborted } from '@air/analytics';
import { type UploadsTrackingPanelProps } from '@air/feature-uploads/ui';
import { isLarge } from '@air/redux-uploader';
import { reportErrorToBugsnag } from '@air/utils-error';

import {
  getContentCollectionUploadAnalyticsParams,
  getUploadAbortedAnalyticsParams,
} from '~/components/Upload/utils/uploadAnalytics';
import { metadataForPublicUploadSelector } from '~/store/publicUploadMetadata/selectors';
import { useAirStore } from '~/utils/ReduxUtils';
import { sendAbortPublicMultipartUpload } from '~/utils/uploadApi/publicUploadApi/publicLargeUploadApi';

export const useContentCollectionUploadDelete = () => {
  const store = useAirStore();
  const { trackUploadAborted } = useTrackUploadAborted();

  const onDeleteUpload = useCallback<Required<UploadsTrackingPanelProps>['onDeleteUpload']>(
    async (upload) => {
      if (isLarge(upload)) {
        try {
          const analyticsParams = getContentCollectionUploadAnalyticsParams({
            context: 'content_collection_upload_manager',
            state: store.getState(),
            upload,
          });

          trackUploadAborted(getUploadAbortedAnalyticsParams({ commonParams: analyticsParams, upload }));
        } catch (analyticsError) {
          reportErrorToBugsnag({ error: analyticsError, context: 'Failed to track upload aborted analytics' });
        }
      }

      if (isLarge(upload) && !!upload.s3Info.uploadUrlInfo) {
        const uploadMetadata = metadataForPublicUploadSelector(store.getState(), upload.id);
        void sendAbortPublicMultipartUpload({
          uploadUrlInfo: upload.s3Info.uploadUrlInfo,
          shortBoardId: uploadMetadata.shortBoardId,
        });
      }
    },
    [store, trackUploadAborted],
  );

  return { onDeleteUpload };
};
